/**
 * Donut Chart Component
 *
 * HTML/CSS-based animated donut chart using conic-gradient.
 * Each segment has separate rounded cap elements for z-index control.
 * Caps are positioned using CSS transforms, staying connected during animation.
 *
 * @package oska
 */

/**
 * DonutChart class
 * @param {HTMLElement} element - The donut chart container with [data-donut-chart]
 */
class DonutChart {
  constructor(element) {
    this.container = element;

    // Chart configuration
    this.size = parseInt(element.dataset.size, 10) || 136;
    this.strokeWidth = parseInt(element.dataset.strokeWidth, 10) || 40;
    this.animate = element.dataset.animate !== 'false';
    this.animationDuration = 800; // ms

    // Parse segments from JSON data attribute: [{color, count}]
    let parsed = [];
    try {
      parsed = JSON.parse(element.dataset.segments || '[]');
    } catch (_) {
      parsed = [];
    }

    this.values = parsed.map((seg) => ({
      color: seg.color || '#0171CF',
      count: parseInt(seg.count, 10) || 0,
    }));

    // Ring dimensions
    this.outerRadius = this.size / 2;
    // Inner radius = outer - stroke width
    this.innerRadius = this.outerRadius - this.strokeWidth;
    // Distance from center to cap center (middle of the ring)
    this.capDistance = this.innerRadius + this.strokeWidth / 2;

    this.hasAnimated = false;
    this.observer = null;
    this.animationFrame = null;

    this.init();
  }

  init() {
    this.render();

    if (this.animate) {
      this.setupIntersectionObserver();
    } else {
      // Show static chart immediately
      this.showStatic();
    }
  }

  /**
   * Calculate segment angles from this.values [{color, count}]
   * Returns array of { color, value, startAngle, endAngle }
   * Filters out zero-count segments and enforces minimum angle for visibility.
   */
  calculateSegments() {
    // Filter out zero-count items first
    const nonZeroItems = this.values.filter((v) => v.count > 0);
    const total = nonZeroItems.reduce((s, v) => s + v.count, 0);
    if (total === 0) return [];

    // Minimum angle in degrees for small segments to remain visible
    const minAngle = 50;
    const segmentCount = nonZeroItems.length;

    // Calculate raw angles
    let rawAngles = nonZeroItems.map((item) => ({
      color: item.color,
      value: item.count,
      rawAngle: (item.count / total) * 360,
    }));

    // Apply minimum angle constraint
    // First pass: identify segments below minimum and calculate how much extra space we need
    let extraNeeded = 0;
    let availablePool = 0;

    rawAngles.forEach((seg) => {
      if (seg.rawAngle < minAngle) {
        extraNeeded += minAngle - seg.rawAngle;
      } else {
        availablePool += seg.rawAngle - minAngle;
      }
    });

    // Second pass: redistribute - boost small segments, shrink large ones proportionally
    const finalAngles = rawAngles.map((seg) => {
      if (seg.rawAngle < minAngle) {
        return { ...seg, angle: minAngle };
      } else if (availablePool > 0 && extraNeeded > 0) {
        const excess = seg.rawAngle - minAngle;
        const reduction = (excess / availablePool) * extraNeeded;
        return { ...seg, angle: seg.rawAngle - reduction };
      }
      return { ...seg, angle: seg.rawAngle };
    });

    // Build segments with start/end angles
    const segments = [];
    let currentAngle = 0;

    for (const item of finalAngles) {
      segments.push({
        color: item.color,
        value: item.value,
        startAngle: currentAngle,
        endAngle: currentAngle + item.angle,
      });
      currentAngle += item.angle;
    }

    return segments;
  }

  /**
   * Create a segment element (ring + caps)
   * All segments start at 0 and animate to their final positions.
   * Colors are applied via --segment-color CSS custom property.
   */
  createSegment(segment, index) {
    const segmentEl = document.createElement('div');
    segmentEl.className = 'donut-chart__segment';
    segmentEl.dataset.index = index;
    segmentEl.style.setProperty('--segment-color', segment.color);

    // Ring element (conic-gradient arc)
    const ring = document.createElement('div');
    ring.className = 'donut-chart__ring';
    ring.style.setProperty('--final-start-angle', `${segment.startAngle}deg`);
    ring.style.setProperty('--final-end-angle', `${segment.endAngle}deg`);
    ring.style.setProperty('--current-start-angle', '0deg');
    ring.style.setProperty('--current-end-angle', '0deg');

    // Start cap -- only the first segment needs a visible start cap;
    // subsequent start caps are hidden behind the previous end cap.
    const startCap = document.createElement('div');
    startCap.className = 'donut-chart__cap donut-chart__cap--start';
    startCap.style.setProperty('--current-angle', '0deg');
    if (index > 0) {
      startCap.style.display = 'none';
    }

    // End cap -- earlier segments get higher z-index so they sit on top
    const endCap = document.createElement('div');
    endCap.className = 'donut-chart__cap donut-chart__cap--end';
    endCap.style.setProperty('--current-angle', '0deg');
    endCap.style.zIndex = 10 - index;

    segmentEl.appendChild(startCap);
    segmentEl.appendChild(ring);
    segmentEl.appendChild(endCap);

    return segmentEl;
  }

  /**
   * Render the donut chart
   */
  render() {
    // Set container size
    this.container.style.setProperty('--size', `${this.size}px`);
    this.container.style.setProperty('--stroke-width', `${this.strokeWidth}px`);
    this.container.style.setProperty('--cap-distance', `${this.capDistance}px`);

    const segments = this.calculateSegments();

    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'donut-chart__wrapper';

    // Create segments (first segment = bottom, last = top for z-index)
    segments.forEach((segment, index) => {
      const segmentEl = this.createSegment(segment, index, segments.length);
      wrapper.appendChild(segmentEl);
    });

    this.container.appendChild(wrapper);
    this.segments = segments;
  }

  /**
   * Setup Intersection Observer for viewport-triggered animation
   */
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.startAnimation();
            this.hasAnimated = true;
            this.observer.disconnect();
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '50px',
      },
    );

    this.observer.observe(this.container);
  }

  /**
   * Start the animation using requestAnimationFrame for smooth cap movement
   * All segments start at 0° and spread out to their final positions
   */
  startAnimation() {
    const startTime = performance.now();
    const duration = this.animationDuration;

    const animateFrame = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      // Update each segment - both start and end angles animate from 0
      this.segments.forEach((segment, index) => {
        const segmentEl = this.container.querySelector(`[data-index="${index}"]`);
        if (!segmentEl) return;

        const ring = segmentEl.querySelector('.donut-chart__ring');
        const startCap = segmentEl.querySelector('.donut-chart__cap--start');
        const endCap = segmentEl.querySelector('.donut-chart__cap--end');

        // Both angles interpolate from 0 to final position
        const currentStartAngle = segment.startAngle * eased;
        const currentEndAngle = segment.endAngle * eased;

        // Update ring
        ring.style.setProperty('--current-start-angle', `${currentStartAngle}deg`);
        ring.style.setProperty('--current-end-angle', `${currentEndAngle}deg`);

        // Update cap positions
        startCap.style.setProperty('--current-angle', `${currentStartAngle}deg`);
        endCap.style.setProperty('--current-angle', `${currentEndAngle}deg`);
      });

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animateFrame);
      }
    };

    this.animationFrame = requestAnimationFrame(animateFrame);
  }

  /**
   * Show static (no animation) - jump to final positions
   */
  showStatic() {
    this.segments.forEach((segment, index) => {
      const segmentEl = this.container.querySelector(`[data-index="${index}"]`);
      if (!segmentEl) return;

      const ring = segmentEl.querySelector('.donut-chart__ring');
      const startCap = segmentEl.querySelector('.donut-chart__cap--start');
      const endCap = segmentEl.querySelector('.donut-chart__cap--end');

      ring.style.setProperty('--current-start-angle', `${segment.startAngle}deg`);
      ring.style.setProperty('--current-end-angle', `${segment.endAngle}deg`);
      startCap.style.setProperty('--current-angle', `${segment.startAngle}deg`);
      endCap.style.setProperty('--current-angle', `${segment.endAngle}deg`);
    });
  }

  /**
   * Reset and replay animation (can be called externally)
   */
  replay() {
    // Cancel any running animation
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    // Reset to start state (all at 0°)
    this.segments.forEach((segment, index) => {
      const segmentEl = this.container.querySelector(`[data-index="${index}"]`);
      if (!segmentEl) return;

      const ring = segmentEl.querySelector('.donut-chart__ring');
      const startCap = segmentEl.querySelector('.donut-chart__cap--start');
      const endCap = segmentEl.querySelector('.donut-chart__cap--end');

      ring.style.setProperty('--current-start-angle', '0deg');
      ring.style.setProperty('--current-end-angle', '0deg');
      startCap.style.setProperty('--current-angle', '0deg');
      endCap.style.setProperty('--current-angle', '0deg');
    });

    // Force reflow
    void this.container.offsetWidth;

    // Start animation
    this.startAnimation();
  }

  /**
   * Update chart with new segment values [{color, count}]
   */
  update(values) {
    if (Array.isArray(values)) {
      this.values = values.map((seg) => ({
        color: seg.color || '#0171CF',
        count: parseInt(seg.count, 10) || 0,
      }));
    }

    // Clear existing wrapper
    const existingWrapper = this.container.querySelector('.donut-chart__wrapper');
    if (existingWrapper) {
      existingWrapper.remove();
    }

    // Re-render
    this.render();

    if (this.animate) {
      this.startAnimation();
    } else {
      this.showStatic();
    }
  }
}

/**
 * Initialize all visible, uninitialized donut charts.
 * Skips charts inside [hidden] containers and already-initialized charts.
 */
function initVisibleCharts(instances) {
  const containers = document.querySelectorAll('[data-donut-chart], [data-chart]');

  containers.forEach((container) => {
    if (container.closest('[hidden]')) return;
    if (container.querySelector('.donut-chart__wrapper')) return;

    const instance = new DonutChart(container);
    instances.set(container, instance);
    container._donutChart = instance;
  });
}

/**
 * Initialize all donut charts and listen for re-init events.
 * @returns {Map<HTMLElement, DonutChart>} Map of element to instance
 */
export default function initDonutCharts() {
  const instances = new Map();

  initVisibleCharts(instances);

  // Expose instances globally for external access
  window.donutChartInstances = instances;

  // Re-init when card sets are toggled
  document.addEventListener('donut-charts:reinit', () => {
    initVisibleCharts(instances);
  });

  return instances;
}
